import { useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { prismLightTheme } from '@/theme/prismTheme';
import {
  FluentProvider,
  makeStyles,
  tokens,
  Title2,
  Body1,
  Body2,
  Caption1,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Card,
} from '@fluentui/react-components';
import {
  Board24Regular,
  People24Regular,
  Briefcase24Regular,
  Apps24Regular,
  Grid24Regular,
  Money24Regular,
  Receipt24Regular,
  Settings24Regular,
  Navigation24Regular,
  SignOut24Regular,
  Add24Regular,
  ArrowSync24Regular,
  Checkmark24Regular,
  Camera24Regular,
  ArrowDownload24Regular,
} from '@fluentui/react-icons';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip as RTooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { formatCurrency, formatDate, getInitials } from '@/utils/formatters';

// ─── Mock Data ───────────────────────────────────────────────────────────────

const DEMO_EMPLOYEES = [
  { id: '1', name: 'Parvesh Kaushal', email: 'parvesh@2sd.co.uk', dept: 'Engineering', title: 'CTO', status: 'Active' as const },
  { id: '2', name: 'Sarah Mitchell', email: 'sarah@2sd.co.uk', dept: 'Engineering', title: 'Senior Developer', status: 'Active' as const },
  { id: '3', name: 'James Wilson', email: 'james@2sd.co.uk', dept: 'Design', title: 'UX Lead', status: 'Active' as const },
  { id: '4', name: 'Emma Thompson', email: 'emma@2sd.co.uk', dept: 'Operations', title: 'Project Manager', status: 'Active' as const },
  { id: '5', name: 'Oliver Brown', email: 'oliver@2sd.co.uk', dept: 'Engineering', title: 'DevOps Engineer', status: 'Active' as const },
  { id: '6', name: 'Amara Okafor', email: 'amara@2sd.co.uk', dept: 'Finance', title: 'Finance Manager', status: 'Active' as const },
  { id: '7', name: 'David Chen', email: 'david@2sd.co.uk', dept: 'Engineering', title: 'Full Stack Dev', status: 'Active' as const },
  { id: '8', name: 'Lisa Park', email: 'lisa@2sd.co.uk', dept: 'Marketing', title: 'Digital Marketer', status: 'Active' as const },
  { id: '9', name: 'Raj Patel', email: 'raj@2sd.co.uk', dept: 'Engineering', title: 'Cloud Architect', status: 'Active' as const },
  { id: '10', name: 'Nina Foster', email: 'nina@2sd.co.uk', dept: 'HR', title: 'HR Manager', status: 'Inactive' as const },
];

const DEMO_PROJECTS = [
  { id: '1', code: 'GG', title: 'GlassGov', status: 'Active', budget: 85000, members: 4, start: '2025-01-15', end: '2026-06-30' },
  { id: '2', code: 'LGY', title: 'Loggy Platform', status: 'Active', budget: 120000, members: 5, start: '2025-03-01', end: '2026-09-30' },
  { id: '3', code: 'SBI', title: 'SemanticBI', status: 'Active', budget: 65000, members: 3, start: '2025-06-01', end: '2026-03-31' },
  { id: '4', code: 'ERA', title: 'ERA CRM Migration', status: 'On Hold', budget: 45000, members: 2, start: '2025-09-01', end: '2026-01-31' },
  { id: '5', code: 'TAP', title: 'TAP Portal', status: 'Completed', budget: 30000, members: 2, start: '2024-06-01', end: '2025-12-31' },
];

const DEMO_APPS = [
  { id: '1', name: 'Azure DevOps', vendor: 'Microsoft', cat: 'DevTools', cost: 5.20, model: 'PerUser', synced: true, assigned: 9 },
  { id: '2', name: 'Visual Studio Enterprise', vendor: 'Microsoft', cat: 'DevTools', cost: 38.50, model: 'PerUser', synced: true, assigned: 5 },
  { id: '3', name: 'MSDN Subscription', vendor: 'Microsoft', cat: 'DevTools', cost: 35.00, model: 'PerUser', synced: true, assigned: 4 },
  { id: '4', name: 'Microsoft 365 Business Basic', vendor: 'Microsoft', cat: 'Productivity', cost: 4.60, model: 'PerUser', synced: true, assigned: 10 },
  { id: '5', name: 'Claude Pro', vendor: 'Anthropic', cat: 'AI', cost: 16.00, model: 'PerUser', synced: false, assigned: 6 },
  { id: '6', name: 'Windsurf Pro', vendor: 'Codeium', cat: 'AI', cost: 12.00, model: 'PerUser', synced: false, assigned: 5 },
  { id: '7', name: 'Figma', vendor: 'Figma', cat: 'Design', cost: 11.00, model: 'PerUser', synced: false, assigned: 3 },
  { id: '8', name: 'Azure Cloud', vendor: 'Microsoft', cat: 'Cloud', cost: 450.00, model: 'PerOrg', synced: true, assigned: 1 },
  { id: '9', name: 'ADO Test Plans', vendor: 'Microsoft', cat: 'DevTools', cost: 43.00, model: 'PerUser', synced: true, assigned: 3 },
  { id: '10', name: 'GitHub Copilot', vendor: 'GitHub', cat: 'AI', cost: 15.00, model: 'PerUser', synced: false, assigned: 7 },
];

const DEMO_EXPENSES = [
  { id: '1', emp: 'Sarah Mitchell', cat: 'Travel', amount: 142.50, date: '2026-04-10', status: 'Approved' as const, photo: true },
  { id: '2', emp: 'James Wilson', cat: 'Equipment', amount: 89.99, date: '2026-04-12', status: 'Submitted' as const, photo: true },
  { id: '3', emp: 'Emma Thompson', cat: 'Training', amount: 299.00, date: '2026-04-08', status: 'Approved' as const, photo: false },
  { id: '4', emp: 'Oliver Brown', cat: 'Software', amount: 49.99, date: '2026-04-14', status: 'Submitted' as const, photo: true },
  { id: '5', emp: 'Parvesh Kaushal', cat: 'Office', amount: 67.30, date: '2026-04-15', status: 'Draft' as const, photo: false },
];

const COLORS = ['#0078d4', '#107c10', '#8764b8', '#ca5010', '#008272', '#e3008c', '#00bcf2'];

// ─── Styles ──────────────────────────────────────────────────────────────────

const useLayoutStyles = makeStyles({
  root: { display: 'flex', minHeight: '100vh', backgroundColor: tokens.colorNeutralBackground2 },
  sidebar: {
    width: '260px', backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex', flexDirection: 'column', flexShrink: 0,
    position: 'fixed' as const, top: 0, left: 0, bottom: 0, zIndex: 100,
    '@media (max-width: 768px)': { transform: 'translateX(-100%)' },
  },
  sidebarOpen: { '@media (max-width: 768px)': { transform: 'translateX(0)', boxShadow: tokens.shadow64 } },
  overlay: {
    display: 'none',
    '@media (max-width: 768px)': { display: 'block', position: 'fixed' as const, inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 99 },
  },
  logo: { padding: '20px 20px 12px', display: 'flex', alignItems: 'center', gap: '12px' },
  logoText: { fontSize: '18px', fontWeight: 700, color: tokens.colorBrandForeground1 },
  nav: { flex: 1, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '2px', overflowY: 'auto' as const },
  navItem: { justifyContent: 'flex-start', gap: '12px', padding: '10px 12px', borderRadius: tokens.borderRadiusMedium, width: '100%', fontWeight: 400, minHeight: '40px' },
  navItemActive: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1, fontWeight: 600 },
  userSection: { padding: '12px 16px', borderTop: `1px solid ${tokens.colorNeutralStroke2}`, display: 'flex', alignItems: 'center', gap: '10px' },
  main: { flex: 1, marginLeft: '260px', display: 'flex', flexDirection: 'column', '@media (max-width: 768px)': { marginLeft: 0 } },
  topbar: {
    display: 'none',
    '@media (max-width: 768px)': { display: 'flex', alignItems: 'center', padding: '8px 16px', backgroundColor: tokens.colorNeutralBackground1, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, gap: '12px' },
  },
  topbarTitle: { flex: 1, fontWeight: 600, color: tokens.colorBrandForeground1 },
  content: { flex: 1, padding: '24px', maxWidth: '1400px', width: '100%', boxSizing: 'border-box' as const, '@media (max-width: 768px)': { padding: '16px' } },
});

const usePageStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' as const, gap: '12px' },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
  card: { padding: '20px', backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge, boxShadow: tokens.shadow4, cursor: 'pointer' },
  cardValue: { fontSize: '32px', fontWeight: 700, color: tokens.colorBrandForeground1, marginTop: '8px' },
  charts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '16px' },
  chartCard: { padding: '20px', backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge, boxShadow: tokens.shadow4, minHeight: '320px' },
  table: { width: '100%', borderCollapse: 'collapse' as const, backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge, boxShadow: tokens.shadow4, overflow: 'hidden' },
  th: { textAlign: 'left' as const, padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: tokens.colorNeutralForeground2, textTransform: 'uppercase' as const, letterSpacing: '0.5px', borderBottom: `2px solid ${tokens.colorNeutralStroke1}` },
  td: { padding: '12px 16px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, fontSize: '14px' },
  tr: { transition: 'background-color 0.1s', cursor: 'pointer' },
  matrixCell: { width: '36px', height: '36px', borderRadius: tokens.borderRadiusMedium, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: '2px' },
  banner: { padding: '8px 16px', backgroundColor: tokens.colorPaletteYellowBackground1, borderRadius: tokens.borderRadiusMedium, marginBottom: '16px', textAlign: 'center' as const },
});

// ─── Nav Items ───────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: <Board24Regular /> },
  { path: '/members', label: 'Members', icon: <People24Regular /> },
  { path: '/projects', label: 'Projects', icon: <Briefcase24Regular /> },
  { path: '/applications', label: 'Applications', icon: <Apps24Regular /> },
  { path: '/access', label: 'Access Matrix', icon: <Grid24Regular /> },
  { path: '/costs', label: 'Cost Center', icon: <Money24Regular /> },
  { path: '/expenses', label: 'Expenses', icon: <Receipt24Regular /> },
  { path: '/settings', label: 'Settings', icon: <Settings24Regular /> },
];

// ─── Demo Layout ─────────────────────────────────────────────────────────────

function DemoLayout() {
  const styles = useLayoutStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentPage = NAV_ITEMS.find((i) => i.path === location.pathname)?.label ?? 'Prism';

  return (
    <div className={styles.root}>
      {sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />}

      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <div className={styles.logo}>
          <svg width="28" height="28" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <polygon points="16,2 30,28 2,28" fill="#5B92E5" opacity="0.85"/>
            <polygon points="16,7 26,26 6,26" fill="#D8A878" opacity="0.6"/>
            <polygon points="16,12 22,24 10,24" fill="#5B92E5" opacity="0.4"/>
          </svg>
          <span className={styles.logoText}>Prism</span>
        </div>
        <Divider />
        <nav className={styles.nav}>
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Button key={item.path} appearance="subtle" icon={item.icon}
                className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}>
                {item.label}
              </Button>
            );
          })}
        </nav>
        <div className={styles.userSection}>
          <Avatar name="Parvesh Kaushal" initials="PK" size={36} color="brand" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Body1 style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Parvesh Kaushal</Body1>
            <Badge appearance="filled" color="brand" size="small">Admin</Badge>
          </div>
          <Tooltip content="Sign out" relationship="label">
            <Button appearance="subtle" icon={<SignOut24Regular />} size="small" />
          </Tooltip>
        </div>
      </aside>

      <main className={styles.main}>
        <div className={styles.topbar}>
          <Button appearance="subtle" icon={<Navigation24Regular />} onClick={() => setSidebarOpen(true)} />
          <span className={styles.topbarTitle}>{currentPage}</span>
          <Avatar name="Parvesh Kaushal" initials="PK" size={28} color="brand" />
        </div>
        <div className={styles.content}><Outlet /></div>
      </main>
    </div>
  );
}

// ─── Demo Pages ──────────────────────────────────────────────────────────────

function DemoBanner() {
  const s = usePageStyles();
  return (
    <div className={s.banner}>
      <Caption1>Demo Mode — Connect Azure AD to use real data. See docs/SETUP.md</Caption1>
    </div>
  );
}

function DemoDashboard() {
  const s = usePageStyles();
  const navigate = useNavigate();
  const totalCost = DEMO_APPS.reduce((sum, a) => sum + a.cost * (a.model === 'PerOrg' ? 1 : a.assigned), 0);
  const allocData = DEMO_PROJECTS.filter(p => p.status === 'Active').map(p => ({ name: p.code, value: p.members }));
  const licenseData = DEMO_APPS.slice(0, 6).map(a => ({
    name: a.name.length > 18 ? a.name.slice(0, 16) + '...' : a.name,
    assigned: a.assigned, available: Math.max(0, 10 - a.assigned),
  }));

  return (
    <div className={s.page}>
      <DemoBanner />
      <div className={s.header}>
        <Title2>Dashboard</Title2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button appearance="subtle" icon={<ArrowSync24Regular />} onClick={() => navigate('/settings')}>Sync Data</Button>
          <Button appearance="primary" icon={<Add24Regular />} onClick={() => navigate('/expenses')}>Submit Expense</Button>
        </div>
      </div>
      <div className={s.cards}>
        <div className={s.card} onClick={() => navigate('/members')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><People24Regular /><Caption1>Active Members</Caption1></div>
          <div className={s.cardValue}>9</div>
        </div>
        <div className={s.card} onClick={() => navigate('/projects')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Briefcase24Regular /><Caption1>Active Projects</Caption1></div>
          <div className={s.cardValue}>3</div>
        </div>
        <div className={s.card} onClick={() => navigate('/costs')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Money24Regular /><Caption1>Monthly Costs</Caption1></div>
          <div className={s.cardValue}>{formatCurrency(totalCost)}</div>
        </div>
        <div className={s.card} onClick={() => navigate('/expenses')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Receipt24Regular /><Caption1>Pending Expenses</Caption1></div>
          <div className={s.cardValue}>2</div>
        </div>
      </div>
      <div className={s.charts}>
        <div className={s.chartCard}>
          <Body1 style={{ fontWeight: 600, marginBottom: 16 }}>Allocation by Project</Body1>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart><Pie data={allocData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
              {allocData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><RTooltip /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className={s.chartCard}>
          <Body1 style={{ fontWeight: 600, marginBottom: 16 }}>License Utilization</Body1>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={licenseData} layout="vertical">
              <XAxis type="number" /><YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} /><RTooltip /><Legend />
              <Bar dataKey="assigned" fill="#0078d4" name="Assigned" stackId="a" />
              <Bar dataKey="available" fill="#c8c8c8" name="Available" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function DemoMembers() {
  const s = usePageStyles();
  return (
    <div className={s.page}>
      <DemoBanner />
      <div className={s.header}>
        <Title2>Members</Title2>
        <Button appearance="primary" icon={<ArrowSync24Regular />}>Sync from Entra ID</Button>
      </div>
      <table className={s.table}>
        <thead><tr>
          <th className={s.th}>Name</th><th className={s.th}>Department</th><th className={s.th}>Job Title</th><th className={s.th}>Status</th>
        </tr></thead>
        <tbody>
          {DEMO_EMPLOYEES.map(e => (
            <tr key={e.id} className={s.tr} style={{ backgroundColor: undefined }}>
              <td className={s.td}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={e.name} initials={getInitials(e.name)} size={32} color="brand" />
                  <div><Body2 style={{ fontWeight: 500 }}>{e.name}</Body2><Caption1>{e.email}</Caption1></div>
                </div>
              </td>
              <td className={s.td}>{e.dept}</td>
              <td className={s.td}>{e.title}</td>
              <td className={s.td}><Badge appearance="filled" color={e.status === 'Active' ? 'success' : 'danger'} size="small">{e.status}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Caption1>{DEMO_EMPLOYEES.length} members</Caption1>
    </div>
  );
}

function DemoProjects() {
  const s = usePageStyles();
  const statusColor = (st: string) => st === 'Active' ? 'success' : st === 'On Hold' ? 'warning' : 'informative';
  return (
    <div className={s.page}>
      <DemoBanner />
      <div className={s.header}><Title2>Projects</Title2><Button appearance="primary" icon={<Add24Regular />}>New Project</Button></div>
      <table className={s.table}>
        <thead><tr>
          <th className={s.th}>Code</th><th className={s.th}>Project</th><th className={s.th}>Status</th><th className={s.th}>Members</th><th className={s.th}>Budget</th><th className={s.th}>Duration</th>
        </tr></thead>
        <tbody>
          {DEMO_PROJECTS.map(p => (
            <tr key={p.id} className={s.tr}>
              <td className={s.td}><Badge appearance="outline" size="small">{p.code}</Badge></td>
              <td className={s.td}><Body2 style={{ fontWeight: 500 }}>{p.title}</Body2></td>
              <td className={s.td}><Badge appearance="filled" color={statusColor(p.status) as 'success' | 'warning' | 'informative'} size="small">{p.status}</Badge></td>
              <td className={s.td}>{p.members} allocated</td>
              <td className={s.td}>{formatCurrency(p.budget)}</td>
              <td className={s.td}><Caption1>{formatDate(p.start)} — {formatDate(p.end)}</Caption1></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DemoApplications() {
  const s = usePageStyles();
  return (
    <div className={s.page}>
      <DemoBanner />
      <div className={s.header}>
        <Title2>Applications</Title2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button appearance="outline" icon={<ArrowSync24Regular />}>Sync Microsoft Licenses</Button>
          <Button appearance="primary" icon={<Add24Regular />}>Add Application</Button>
        </div>
      </div>
      <table className={s.table}>
        <thead><tr>
          <th className={s.th}>Application</th><th className={s.th}>Category</th><th className={s.th}>License</th><th className={s.th}>Cost/License</th><th className={s.th}>Assigned</th><th className={s.th}>Source</th>
        </tr></thead>
        <tbody>
          {DEMO_APPS.map(a => (
            <tr key={a.id} className={s.tr}>
              <td className={s.td}><div><Body2 style={{ fontWeight: 500 }}>{a.name}</Body2><Caption1>{a.vendor}</Caption1></div></td>
              <td className={s.td}><Badge appearance="outline" size="small">{a.cat}</Badge></td>
              <td className={s.td}>{a.model}</td>
              <td className={s.td}>{formatCurrency(a.cost)}/mo</td>
              <td className={s.td}>{a.assigned} users</td>
              <td className={s.td}>{a.synced ? <Badge appearance="filled" color="brand" size="small">Microsoft</Badge> : <Badge appearance="tint" size="small">Manual</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DemoAccessMatrix() {
  const s = usePageStyles();
  const activeEmps = DEMO_EMPLOYEES.filter(e => e.status === 'Active');
  // Random assignment matrix
  const isAssigned = (empIdx: number, appIdx: number) => (empIdx * 7 + appIdx * 3) % 3 !== 0;

  return (
    <div className={s.page}>
      <DemoBanner />
      <div className={s.header}><Title2>Access Matrix</Title2><Caption1>{activeEmps.length} members x {DEMO_APPS.length} apps</Caption1></div>
      <div style={{ backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusLarge, boxShadow: tokens.shadow4, overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
        <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%' }}>
          <thead>
            <tr>
              <th className={s.th} style={{ position: 'sticky', left: 0, backgroundColor: tokens.colorNeutralBackground1, zIndex: 3, minWidth: 180 }}>Employee</th>
              {DEMO_APPS.map(a => (
                <th key={a.id} style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', height: 120, padding: '8px 6px', fontSize: 11, fontWeight: 600, borderBottom: `2px solid ${tokens.colorNeutralStroke1}`, verticalAlign: 'bottom' }}>
                  {a.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeEmps.map((emp, ei) => (
              <tr key={emp.id}>
                <td style={{ padding: '8px 14px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, position: 'sticky', left: 0, backgroundColor: tokens.colorNeutralBackground1, zIndex: 1 }}>
                  <Body1 style={{ fontWeight: 500 }}>{emp.name}</Body1>
                  <Caption1 style={{ display: 'block' }}>{emp.dept}</Caption1>
                </td>
                {DEMO_APPS.map((_, ai) => {
                  const assigned = isAssigned(ei, ai);
                  return (
                    <td key={ai} style={{ textAlign: 'center', padding: 4, borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
                      <div className={s.matrixCell} style={{
                        backgroundColor: assigned ? '#107c10' : tokens.colorNeutralBackground3,
                        color: assigned ? 'white' : 'transparent',
                        cursor: 'pointer',
                      }}>
                        {assigned && <Checkmark24Regular style={{ fontSize: 16 }} />}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${tokens.colorNeutralStroke2}`, display: 'flex', gap: 16, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#107c10' }} /><Caption1>Assigned</Caption1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: tokens.colorNeutralBackground3, border: `1px solid ${tokens.colorNeutralStroke2}` }} /><Caption1>Not assigned</Caption1>
          </div>
          <Caption1 style={{ marginLeft: 'auto' }}>Click a cell to assign or revoke</Caption1>
        </div>
      </div>
    </div>
  );
}

function DemoCostCenter() {
  const s = usePageStyles();
  const costByDept = [
    { name: 'Engineering', cost: 1850 }, { name: 'Design', cost: 320 },
    { name: 'Operations', cost: 180 }, { name: 'Finance', cost: 95 },
    { name: 'Marketing', cost: 120 }, { name: 'HR', cost: 45 },
  ];
  const costByCat = [
    { name: 'DevTools', value: 980 }, { name: 'AI', value: 560 },
    { name: 'Productivity', value: 310 }, { name: 'Cloud', value: 450 },
    { name: 'Design', value: 120 },
  ];
  const totalCost = DEMO_APPS.reduce((sum, a) => sum + a.cost * (a.model === 'PerOrg' ? 1 : a.assigned), 0);

  return (
    <div className={s.page}>
      <DemoBanner />
      <Title2>Cost Center</Title2>
      <div className={s.cards}>
        <div className={s.card}><Caption1>Total Monthly Cost</Caption1><div className={s.cardValue}>{formatCurrency(totalCost)}</div></div>
        <div className={s.card}><Caption1>Avg Cost / Employee</Caption1><div className={s.cardValue}>{formatCurrency(totalCost / 9)}</div></div>
        <div className={s.card}><Caption1>Highest Department</Caption1><div className={s.cardValue}>Engineering</div></div>
        <div className={s.card}><Caption1>Total Applications</Caption1><div className={s.cardValue}>{DEMO_APPS.length}</div></div>
      </div>
      <div className={s.charts}>
        <div className={s.chartCard}>
          <Body1 style={{ fontWeight: 600, marginBottom: 16 }}>Cost by Department</Body1>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={costByDept}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis /><RTooltip formatter={(v: number) => formatCurrency(v)} /><Bar dataKey="cost" fill="#0078d4" name="Monthly Cost" /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className={s.chartCard}>
          <Body1 style={{ fontWeight: 600, marginBottom: 16 }}>Cost by Category</Body1>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart><Pie data={costByCat} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label={({ name }) => name}>
              {costByCat.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><RTooltip formatter={(v: number) => formatCurrency(v)} /><Legend /></PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function DemoExpenses() {
  const s = usePageStyles();
  const statusColor = (st: string) => st === 'Approved' ? 'success' : st === 'Submitted' ? 'warning' : st === 'Rejected' ? 'danger' : 'informative';

  return (
    <div className={s.page}>
      <DemoBanner />
      <div className={s.header}>
        <Title2>Expenses</Title2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button appearance="outline" icon={<ArrowDownload24Regular />}>Download Excel</Button>
          <Button appearance="primary" icon={<Add24Regular />}>Submit Expense</Button>
        </div>
      </div>
      <table className={s.table}>
        <thead><tr>
          <th className={s.th}>Date</th><th className={s.th}>Employee</th><th className={s.th}>Category</th><th className={s.th}>Amount</th><th className={s.th}>Bill</th><th className={s.th}>Status</th><th className={s.th}>Actions</th>
        </tr></thead>
        <tbody>
          {DEMO_EXPENSES.map(e => (
            <tr key={e.id} className={s.tr}>
              <td className={s.td}><Caption1>{formatDate(e.date)}</Caption1></td>
              <td className={s.td}>{e.emp}</td>
              <td className={s.td}><Badge appearance="outline" size="small">{e.cat}</Badge></td>
              <td className={s.td}><Body1 style={{ fontWeight: 600 }}>{formatCurrency(e.amount)}</Body1></td>
              <td className={s.td}>{e.photo ? <Camera24Regular /> : '—'}</td>
              <td className={s.td}><Badge appearance="filled" color={statusColor(e.status) as 'success' | 'warning' | 'danger' | 'informative'} size="small">{e.status}</Badge></td>
              <td className={s.td}>
                {e.status === 'Submitted' && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <Button appearance="primary" size="small" icon={<Checkmark24Regular />}>Approve</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DemoSettings() {
  const s = usePageStyles();
  return (
    <div className={s.page}>
      <DemoBanner />
      <Title2>Settings</Title2>
      <Card style={{ padding: 24 }}>
        <Body1 style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><ArrowSync24Regular /> Data Sync</Body1>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${tokens.colorNeutralStroke3}` }}>
          <div><Body1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><People24Regular /> Sync Users from Entra ID</Body1><Caption1>Pull employee data from Microsoft Entra ID</Caption1></div>
          <Button appearance="primary" icon={<ArrowSync24Regular />}>Sync Users</Button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
          <div><Body1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Apps24Regular /> Sync Microsoft Licenses</Body1><Caption1>Pull subscription and license data from Microsoft Admin</Caption1></div>
          <Button appearance="primary" icon={<ArrowSync24Regular />}>Sync Licenses</Button>
        </div>
      </Card>
      <Card style={{ padding: 24 }}>
        <Body1 style={{ fontWeight: 600, marginBottom: 16 }}>About</Body1>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
          <Caption1>App</Caption1><Body1>Prism v1.0.0</Body1>
          <Caption1>User</Caption1><Body1>parvesh@2sd.co.uk</Body1>
          <Caption1>Role</Caption1><Badge appearance="filled" color="brand" size="small">Admin</Badge>
          <Caption1>Mode</Caption1><Badge appearance="tint" color="warning" size="small">Demo</Badge>
        </div>
      </Card>
    </div>
  );
}

// ─── Demo App Root ───────────────────────────────────────────────────────────

export function DemoApp() {
  return (
    <FluentProvider theme={prismLightTheme}>
      <BrowserRouter>
        <Routes>
          <Route element={<DemoLayout />}>
            <Route path="/" element={<DemoDashboard />} />
            <Route path="/members" element={<DemoMembers />} />
            <Route path="/projects" element={<DemoProjects />} />
            <Route path="/applications" element={<DemoApplications />} />
            <Route path="/access" element={<DemoAccessMatrix />} />
            <Route path="/costs" element={<DemoCostCenter />} />
            <Route path="/expenses" element={<DemoExpenses />} />
            <Route path="/settings" element={<DemoSettings />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </FluentProvider>
  );
}
